import { rowToItem, useTickerStore } from '@/app/store/ticker'
import { httpStatusOf, tickerApi, type TickerLevel, type TickerNewEvent } from './api'

/**
 * Binds the ticker to the always-on presence socket (docs/ticker-design.md §3.1, §5.1).
 *
 * On every `connect`, and whenever the chosen sets, the level or the on/off switch change:
 *   1. buffering starts (the store holds live events rather than showing them);
 *   2. any set not watched before is PRIMED: its floor becomes "now", so choosing a set does not
 *      replay its history. "Now" is the newest id across every watched set, or the highest id
 *      this page has already met anywhere, whichever is higher: ids are global and committed in
 *      order (§3.2), so a quiet or empty set is primed at the live edge too, never at 0. This
 *      happens before joining, so nothing committed after the prime can fall under the floor;
 *   3. `ticker:watch { setIds, level }` joins the rooms (re-sent on every connect, because room
 *      membership is per connection and socket.io reuses the client object across a reconnect);
 *   4. ONE backfill page (`after = cursor - 50`, deduped by id) fetches what the socket missed.
 *      The cursor is per set: the higher of lastSeenId and that set's floor, so a set chosen just
 *      now reads from its own floor, never through the history that came before it. Sets that
 *      share a cursor share the read. If more was missed than a page holds, the rows are skipped:
 *      the cursor jumps to the newest id and the conveyor shows one "N messages while you were
 *      away" card instead, counting only the kinds the level admits;
 *   5. the backfill and the buffer are merged in id order, and only then is the stream trusted.
 *
 * Failures: a refused watch is retried with backoff unless the refusal is about the permission
 * (the ticker then hides) or the sign-in (the next connect tries again). A failed REST read is
 * retried with backoff, except a 401 or 403, which stops it. Each failure is recorded in the
 * store's `watchError`, which the dropdown puts into words.
 *
 * `ticker:revoked { message }` is the server saying the user's role has lost `ticker.view` while
 * watching; it has already taken the socket out of every ticker room. It is a permission refusal:
 * the ticker hides and is not re-watched until /auth/me grants the permission again (the store).
 *
 * Switching the ticker off, or choosing no sets, sends `ticker:unwatch`.
 */

/** The slice of a socket.io client this needs; the tests drive it with a fake. */
/* eslint-disable @typescript-eslint/no-explicit-any -- socket.io's own listener signature */
export interface TickerSocket {
    connected: boolean
    on(event: string, handler: (...args: any[]) => void): unknown
    off(event: string, handler: (...args: any[]) => void): unknown
    emit(event: string, ...args: any[]): unknown
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface TickerConnectionOptions {
    /** How long to wait for the watch ack before backfilling anyway. */
    ackTimeoutMs?: number
    /** The first retry's delay; each further retry doubles it. */
    retryMs?: number
    /** The longest a retry waits. */
    maxRetryMs?: number
    /** Coalesce rapid preference changes (ticking several sets) into one re-watch. */
    prefsDebounceMs?: number
}

/**
 * The server's ack. `code` says whether a refusal is worth retrying; until the server sends it,
 * the refusal's wording decides (refusalOf).
 */
type WatchAck = { ok: true; rooms?: string[] } | { ok: false; error?: string; code?: string }

/** One page of backfill; more missed than this and the conveyor catches up instead of replaying. */
export const BACKFILL_PAGE = 200
const BACKFILL_OVERLAP = 50

export type WatchRefusal = 'forbidden' | 'unauthenticated' | 'transient' | 'superseded' | 'invalid'

/**
 * Why the server refused ticker:watch. Only a refusal about the permission is final. One about
 * the sign-in cannot succeed on this connection, but the next connect tries again. Anything else
 * - the per-socket throttle, "not available just now", a database error - is transient.
 */
export function refusalOf(ack: { error?: string; code?: string }): WatchRefusal {
    if (ack.code === 'forbidden') return 'forbidden'
    if (ack.code === 'unauthenticated') return 'unauthenticated'
    if (ack.code === 'retry') return 'transient'
    // A later watch from this client overtook this one: it decides, so this refusal means nothing.
    if (ack.code === 'superseded') return 'superseded'
    // The client sent something the server rejects; retrying the same request cannot help.
    if (ack.code === 'invalid') return 'invalid'
    const error = ack.error ?? ''
    if (/not authenticated|sign-in has expired/i.test(error)) return 'unauthenticated'
    if (/permission|forbidden/i.test(error)) return 'forbidden'
    return 'transient'
}

function emitWithAck(socket: TickerSocket, event: string, payload: unknown, timeoutMs: number): Promise<WatchAck | null> {
    return new Promise((resolve) => {
        let done = false
        const timer = setTimeout(() => {
            if (done) return
            done = true
            resolve(null)
        }, timeoutMs)
        socket.emit(event, payload, (ack: WatchAck) => {
            if (done) return
            done = true
            clearTimeout(timer)
            resolve(ack ?? null)
        })
    })
}

interface Watch {
    setIds: number[]
    level: TickerLevel
}

function desiredWatch(): Watch | null {
    const state = useTickerStore.getState()
    const prefs = state.prefs
    if (state.forbidden || !prefs || !prefs.enabled || prefs.recoverySetIds.length === 0) return null
    return { setIds: prefs.recoverySetIds.slice(), level: prefs.level }
}

const watchKey = (w: Watch | null) => (w ? `${w.level}|${w.setIds.join(',')}` : 'off')

/** Start the ticker on this socket. Returns the teardown. */
export function connectTicker(socket: TickerSocket, options: TickerConnectionOptions = {}): () => void {
    const { ackTimeoutMs = 5000, retryMs = 5000, maxRetryMs = 60_000, prefsDebounceMs = 300 } = options
    const store = useTickerStore
    let disposed = false
    let watching = false
    let currentKey = watchKey(desiredWatch())
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let debounceTimer: ReturnType<typeof setTimeout> | undefined
    let failures = 0

    const unwatch = () => {
        if (watching && socket.connected) socket.emit('ticker:unwatch', {}, () => {})
        watching = false
    }

    const retryLater = () => {
        const delay = Math.min(retryMs * 2 ** failures, maxRetryMs)
        failures += 1
        retryTimer = setTimeout(() => void sync(), delay)
    }

    async function sync() {
        clearTimeout(retryTimer)
        if (disposed) return
        const want = desiredWatch()
        currentKey = watchKey(want)
        if (!want) {
            unwatch()
            // A refusal has already retired the sync; keep its reason.
            if (!store.getState().forbidden) store.getState().stopSync()
            return
        }
        if (!socket.connected) return

        const gen = store.getState().beginSync()
        const stale = () => disposed || store.getState().syncGen !== gen
        try {
            const fresh = want.setIds.filter((id) => !(id in store.getState().floors))
            if (fresh.length) {
                // "Now", for a set being chosen: the newest row in any watched set, or anything
                // newer this page already knows of, since ids are global.
                const newest = await tickerApi.history(want.setIds, { limit: 1 })
                if (stale()) return
                const now = Math.max(newest.items[0]?.id ?? 0, store.getState().highestKnownId)
                store.getState().setFloors(fresh, now)
            }

            const ack = await emitWithAck(socket, 'ticker:watch', { setIds: want.setIds, level: want.level }, ackTimeoutMs)
            if (stale()) return
            if (ack && ack.ok === false) {
                const refusal = refusalOf(ack)
                if (refusal === 'superseded') return
                if (refusal === 'invalid') {
                    store.getState().failSync(gen, 'invalid')
                    return
                }
                if (refusal === 'transient') {
                    // Keep buffering: the retry's own backfill decides what was missed. `watching`
                    // is left as it was: a transient refusal leaves the socket's rooms as they were.
                    store.getState().noteTrouble(gen, 'unavailable')
                    retryLater()
                    return
                }
                // The server leaves every ticker room when it refuses the permission or the sign-in.
                watching = false
                store.getState().failSync(gen, refusal)
                if (refusal === 'forbidden') store.getState().markForbidden('refused')
                return
            }
            watching = true

            // Each set reads from its own cursor: past what this page has seen, and never at or
            // below its floor, since nothing there is ever shown. One shared cursor (the lowest
            // floor) made a set chosen just now read its own history from a quiet set's floor:
            // its pre-choice rows filled the page, and the catch-up counted them as missed.
            const current = store.getState()
            const groups = new Map<number, number[]>()
            for (const id of want.setIds) {
                const from = Math.max(current.lastSeenId, current.floors[id] ?? 0)
                groups.set(from, [...(groups.get(from) ?? []), id])
            }
            const reads = await Promise.all(
                Array.from(groups, async ([from, setIds]) => ({
                    from,
                    setIds,
                    page: await tickerApi.backfill(setIds, Math.max(0, from - BACKFILL_OVERLAP), BACKFILL_PAGE),
                })),
            )
            if (stale()) return
            if (reads.every((read) => !read.page.hasMore)) {
                store.getState().completeSync(gen, reads.flatMap((read) => read.page.items.map(rowToItem)))
            } else {
                // Away too long to replay: skip to the newest message. The count is taken first,
                // so the jump can only land at or above everything it counted. Each group counts
                // from its own cursor, and only the kinds the level would have shown.
                const counts = await Promise.all(reads.map((read) => tickerApi.count(read.setIds, read.from, want.level)))
                if (stale()) return
                const missed = counts.reduce((sum, { count }) => sum + count, 0)
                const newest = await tickerApi.history(want.setIds, { limit: 1 })
                if (stale()) return
                const cursor = Math.max(...reads.map((read) => read.from))
                store.getState().completeSync(gen, [], { newestId: newest.items[0]?.id ?? cursor, missed })
            }
            failures = 0
        } catch (error) {
            if (stale()) return
            const status = httpStatusOf(error)
            if (status === 401 || status === 403) {
                // Refused, not failed: retrying cannot help. A 403 has already hidden the ticker
                // (the API marks it forbidden); a 401 is the session ending.
                store.getState().failSync(gen, status === 403 ? 'forbidden' : 'unauthenticated')
                return
            }
            // Keep buffering and try again. Flushing the buffer now would move the cursor past
            // whatever the failed backfill was meant to find; the retry's own backfill decides.
            store.getState().noteTrouble(gen, 'unavailable')
            retryLater()
        }
    }

    const onConnect = () => {
        failures = 0
        void sync()
    }
    const onNew = (event: TickerNewEvent) => {
        if (!event || typeof event.id !== 'number') return
        store.getState().receiveLive(event)
    }
    // The event carries a message; the store records the code, and the ticker hides either way.
    const onRevoked = () => {
        clearTimeout(retryTimer)
        clearTimeout(debounceTimer)
        // The server has already left the rooms; there is nothing to unwatch.
        watching = false
        const state = store.getState()
        state.failSync(state.syncGen, 'revoked')
        state.markForbidden('refused')
    }

    socket.on('connect', onConnect)
    socket.on('ticker:new', onNew)
    socket.on('ticker:revoked', onRevoked)

    const unsubscribe = store.subscribe((state, prev) => {
        if (state.prefs === prev.prefs && state.forbidden === prev.forbidden) return
        if (watchKey(desiredWatch()) === currentKey) return
        clearTimeout(debounceTimer)
        failures = 0
        debounceTimer = setTimeout(() => void sync(), prefsDebounceMs)
    })

    if (socket.connected) void sync()

    return () => {
        disposed = true
        clearTimeout(retryTimer)
        clearTimeout(debounceTimer)
        unsubscribe()
        socket.off('connect', onConnect)
        socket.off('ticker:new', onNew)
        socket.off('ticker:revoked', onRevoked)
        unwatch()
    }
}

export interface TickerAccessRecheckOptions {
    /** The first re-ask after a 403; each further one doubles it. */
    firstMs?: number
    /** The longest between re-asks. */
    maxMs?: number
}

/**
 * While the ticker is hidden by a REST 403 (forbiddenBy 'request'), ask again: after `firstMs`,
 * doubling to `maxMs`, and at once whenever the socket (re)connects. The ask is one guarded read;
 * a success lifts the refusal (api.ts, onAllowed), after which the ticker mounts and watches
 * again. A refusal the socket server made after reading the role ('refused') is not re-asked.
 *
 * The connection itself is torn down while the ticker is hidden, so this lives outside it.
 * Returns the teardown.
 */
export function recheckTickerAccess(socket: TickerSocket | null, options: TickerAccessRecheckOptions = {}): () => void {
    const { firstMs = 30_000, maxMs = 300_000 } = options
    const store = useTickerStore
    let timer: ReturnType<typeof setTimeout> | undefined
    let delay = firstMs
    let asking = false
    let disposed = false

    const hiddenByRequest = () => {
        const state = store.getState()
        return state.forbidden && state.forbiddenBy === 'request'
    }

    const ask = async () => {
        clearTimeout(timer)
        timer = undefined
        if (disposed || asking || !hiddenByRequest()) return
        asking = true
        try {
            await tickerApi.recoverySets()
        } catch {
            // Still refused, or unreachable: ask again later.
        } finally {
            asking = false
        }
        schedule()
    }

    function schedule() {
        clearTimeout(timer)
        timer = undefined
        if (disposed || !hiddenByRequest()) return
        timer = setTimeout(() => void ask(), delay)
        delay = Math.min(delay * 2, maxMs)
    }

    const onConnect = () => {
        delay = firstMs
        void ask()
    }

    const unsubscribe = store.subscribe((state, prev) => {
        if (state.forbidden === prev.forbidden && state.forbiddenBy === prev.forbiddenBy) return
        if (hiddenByRequest()) {
            delay = firstMs
            schedule()
        } else {
            clearTimeout(timer)
            timer = undefined
        }
    })
    socket?.on('connect', onConnect)
    schedule()

    return () => {
        disposed = true
        clearTimeout(timer)
        unsubscribe()
        socket?.off('connect', onConnect)
    }
}
